import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// Tailwind v4 entry (@import "tailwindcss"). MUST be imported here or vite emits
// NO css and the whole SPA ships unstyled.
import './index.css';
import { clerkAppearance } from './ad/clerk-appearance';
import { ToastProvider } from './components/ui/toast';
import { TenantProvider } from './lib/auth';
import { routeTree } from './routeTree.gen';

// SPA bootstrap. Wires the providers the dashboard needs — Clerk (auth),
// TanStack Query (server cache), TenantProvider (active-tenant context that
// lib/api.ts reads for the X-Tenant-Id header), TanStack Router (navigation).
// File-based routes live in src/routes/* and the @tanstack/router-plugin
// regenerates routeTree.gen during dev/build. Live auth is injected into the
// router context via RouterProvider's `context` prop (see InnerApp) so route
// guards in _authenticated.tsx can read context.auth.isSignedIn.
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? '';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
    },
  },
});

// Created once with a placeholder auth context; the live value is supplied by
// RouterProvider below without recreating the router.
const router = createRouter({
  routeTree,
  context: { auth: { isSignedIn: false } },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const { isLoaded, isSignedIn } = useAuth();
  // CRITICAL: wait for Clerk to hydrate before mounting the router. Until
  // `isLoaded`, `isSignedIn` is `undefined` → coerced to `false` → the
  // `_authenticated` guard would redirect a genuinely-signed-in user to
  // /sign-in, and Clerk's post-load redirect bounces them back → redirect loop.
  if (!isLoaded) {
    return (
      <div className="flex min-h-svh items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  return <RouterProvider router={router} context={{ auth: { isSignedIn } }} />;
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('#root not found');
}

createRoot(rootEl).render(
  <StrictMode>
    <ClerkProvider appearance={clerkAppearance} publishableKey={PUBLISHABLE_KEY}>
      <QueryClientProvider client={queryClient}>
        <TenantProvider>
          <ToastProvider>
            <InnerApp />
          </ToastProvider>
        </TenantProvider>
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
);
